// Chamadas das telas v2. Não troca o window.fetch (o v1 troca no global.js); o 401 vira o aviso de sessão.
import { avisarErro } from './aviso';
import { config } from './config';

let _aoExpirarSessao = null;
let _renovacaoAf = null;
let _avisouRecarregar = false;

export function aoExpirarSessao(funcao) {
    _aoExpirarSessao = funcao;
}

// Token antiforgery novo pela mesma sessão (o #v2-config traz o endereço só na página que escreve). Várias chamadas ao mesmo
// tempo esperam a mesma renovação.
function _renovarAntiforgery() {
    const endereco = config().afNovo;
    if (!endereco) {
        return Promise.resolve(false);
    }
    if (!_renovacaoAf) {
        _renovacaoAf = fetch(endereco, { credentials: 'same-origin', cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(r => (r.ok ? r.json() : null))
            .then(j => {
                if (j?.af) {
                    config().af = j.af;
                    return true;
                }
                return false;
            })
            .catch(() => false)
            .finally(() => setTimeout(() => { _renovacaoAf = null; }, 0));
    }
    return _renovacaoAf;
}

// Leitura sem resposta em 20 s desiste: com o servidor travado, a roda e o esqueleto ficariam até a borda desistir (~100 s).
const _TEMPO_LEITURA_MS = 20000;

// O tempo esgotado sai como TimeoutError, nunca AbortError: as telas ignoram em silêncio o AbortError (cancelamento delas).
async function _comTempo(ms, sinal, tarefa) {
    const controle = new AbortController();
    let estourou = false;
    const espera = setTimeout(() => {
        estourou = true;
        controle.abort();
    }, ms);
    const repassar = () => controle.abort();
    if (sinal?.aborted) {
        controle.abort();
    } else {
        sinal?.addEventListener('abort', repassar, { once: true });
    }
    try {
        return await tarefa(controle.signal);
    } catch (e) {
        if (estourou) {
            const erro = new Error('tempo');
            erro.name = 'TimeoutError';
            throw erro;
        }
        throw e;
    } finally {
        clearTimeout(espera);
        sinal?.removeEventListener('abort', repassar);
    }
}

// silencioso: o 401 não abre o aviso de sessão (o pulso de Mensagens e o selo do topo só param). O erro leva o status.
async function _requisitar(endereco, opcoes = {}, { silencioso = false, repetido = false } = {}) {
    const cabecalhos = { 'X-Requested-With': 'XMLHttpRequest', ...(opcoes.headers || {}) };
    // Antiforgery em todo POST: o token só existe no #v2-config da página que escreve com o cookie (Mensagens, Configurações, Início).
    const af = opcoes.method === 'POST' ? config().af : null;
    if (af) {
        cabecalhos.RequestVerificationToken = af;
    }
    const resposta = await fetch(endereco, {
        credentials: 'same-origin',
        ...opcoes,
        headers: cabecalhos
    });

    // 400 com o token: ele ficou velho (as claims da sessão mudaram em outra aba). Um token novo e UMA repetição; se não resolver,
    // o aviso de recarregar a página (uma vez por página), em vez de a tela parar calada.
    if (resposta.status === 400 && af) {
        if (!repetido && await _renovarAntiforgery()) {
            return _requisitar(endereco, opcoes, { silencioso, repetido: true });
        }
        if (!_avisouRecarregar && config().textos?.recarregar) {
            _avisouRecarregar = true;
            avisarErro(config().textos.recarregar);
        }
    }

    // A sessão que acabou num redirecionamento para o login (o fetch segue o 302 e traria a página de login como conteúdo).
    const status = resposta.redirected && /\/(login|Redirecionamentos)$/i.test(new URL(resposta.url).pathname) ? 401 : resposta.status;

    if (status === 401 && _aoExpirarSessao && !silencioso) {
        _aoExpirarSessao();
    }

    if (!resposta.ok || status === 401) {
        const erro = new Error(`HTTP ${status}`);
        erro.status = status;
        throw erro;
    }

    return resposta;
}

// semCache: dado pessoal (login, botão de candidatura). O resto pode vir do cache do navegador e da borda.
export function obterJson(endereco, { semCache = false, sinal, silencioso = false } = {}) {
    return _comTempo(_TEMPO_LEITURA_MS, sinal, async s => (await _requisitar(endereco, { cache: semCache ? 'no-store' : 'default', signal: s }, { silencioso })).json());
}

export function obterHtml(endereco, { sinal, semCache = false } = {}) {
    return _comTempo(_TEMPO_LEITURA_MS, sinal, async s => {
        const texto = await (await _requisitar(endereco, { signal: s, ...(semCache ? { cache: 'no-store' } : {}) })).text();
        // Todo pedaço é fragmento: uma página inteira no lugar dele (login, página de um proxy) é falha, nunca conteúdo da tela.
        if (/^\s*<(!doctype|html)/i.test(texto)) {
            throw new Error('pagina inteira');
        }
        return texto;
    });
}

// Formulário (os endpoints de hoje leem campo a campo, como o ConfirmarCandidatura). tempo (ms): só para POST que pode ser
// repetido sem efeito (o pulso); nos outros, desistir no navegador não desfaz o que o servidor ainda grava.
export async function enviarFormulario(endereco, dados, { silencioso = false, tempo = 0 } = {}) {
    const enviar = async sinal => (await _requisitar(endereco, { method: 'POST', cache: 'no-store', body: dados, signal: sinal }, { silencioso })).json();
    return tempo ? _comTempo(tempo, null, enviar) : enviar();
}

// Upload com progresso (o fetch não informa o envio): mesmo antiforgery e a mesma renovação única no 400. Devolve { promessa, abortar };
// abortar rejeita com erro.abortado = true. aoProgresso(0–100).
export function enviarComProgresso(endereco, dados, aoProgresso) {
    let xhr = null;
    let abortado = false;
    const tentar = repetido => new Promise((resolver, rejeitar) => {
        xhr = new XMLHttpRequest();
        xhr.open('POST', endereco);
        xhr.responseType = 'json';
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        const af = config().af;
        if (af) {
            xhr.setRequestHeader('RequestVerificationToken', af);
        }
        xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable && aoProgresso) {
                aoProgresso(Math.round((e.loaded / e.total) * 100));
            }
        });
        xhr.addEventListener('load', async () => {
            if (xhr.status === 400 && af && !repetido && await _renovarAntiforgery()) {
                tentar(true).then(resolver, rejeitar);
                return;
            }
            if (xhr.status === 401 && _aoExpirarSessao) {
                _aoExpirarSessao();
            }
            if (xhr.status < 200 || xhr.status >= 300) {
                const erro = new Error(`HTTP ${xhr.status}`);
                erro.status = xhr.status;
                rejeitar(erro);
                return;
            }
            resolver(xhr.response);
        });
        xhr.addEventListener('error', () => rejeitar(new Error('rede')));
        xhr.addEventListener('abort', () => {
            const erro = new Error('abortado');
            erro.abortado = true;
            rejeitar(erro);
        });
        if (abortado) {
            xhr.abort();
            return;
        }
        xhr.send(dados);
    });
    return {
        promessa: tentar(false),
        abortar: () => {
            abortado = true;
            xhr?.abort();
        }
    };
}

export async function enviarJson(endereco, corpo, { silencioso = false } = {}) {
    const resposta = await _requisitar(endereco, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo)
    }, { silencioso });
    return resposta.json();
}
